import NewsletterSignupForm from "@/components/NewsletterSignupForm";
import { NEWSLETTER_COPY as COPY, newsletterSignupEnabled } from "@/lib/newsletterConfig";

/**
 * The newsletter signup as it appears on tool pages (home, ESID lookup, bill check): the SAME form component with the SAME
 * approved copy and the SAME unchecked consent checkbox as /newsletter, in a quiet panel at the foot of the page.
 *
 * Placement rules (Owner order 2026-10-11): below the primary action and its result, never above the tool; static markup
 * (no popup, no modal, no timer, no scroll trigger, nothing injected after load) so it cannot shift the content above it;
 * exactly one per page. Renders nothing unless REACT_APP_NEWSLETTER_SIGNUP_ENABLED=true (the operational switch).
 *
 * `utility` is the delivery-utility code the page already knows (the bill's detected utility, the ESID lookup's TDU). It only
 * pre-selects the optional utility field; the visitor can change it. The newsletter consent stays on its own: it never shares
 * a checkbox or a submit action with the bill-check consent or the ESID form (CLO).
 * `placement` is a layout/analytics label only; it is not sent to the server.
 */
export default function NewsletterSignupBlock({ placement, utility = null, contained = false }) {
  if (!newsletterSignupEnabled()) return null;
  const headingId = `newsletter-block-heading-${placement}`;
  const panel = (
    <div className="max-w-[760px] rounded-2xl border border-slate-200 bg-slate-50/60 p-6 md:p-8">
      <h2 id={headingId} className="font-display text-2xl font-extrabold tracking-tight text-[#0A2540] mb-2">{COPY.heading}</h2>
      <p className="text-slate-700 mb-6 max-w-[60ch]" data-testid="newsletter-block-intro">{COPY.intro}</p>
      <NewsletterSignupForm compact defaultUtility={utility} placement={placement} />
    </div>
  );
  // `contained`: the page already provides a narrow padded column (bill check), so no second wrapper or rule.
  return contained ? (
    <section aria-labelledby={headingId} className="mt-14" data-testid="newsletter-block" data-placement={placement}>
      {panel}
    </section>
  ) : (
    <section aria-labelledby={headingId} className="border-t border-slate-100" data-testid="newsletter-block" data-placement={placement}>
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-14">{panel}</div>
    </section>
  );
}
